/**
 * @fileoverview Built-in Dashboard cards (MOR-906 §7.8)
 *
 * The firm metrics — actual and chargeable time, chargeable value,
 * utilisation, realisation, work awaiting approval — and the daily
 * actual-vs-chargeable chart, expressed as cards the board can place,
 * resize and remove like any saved widget.
 *
 * They are computed rather than saved: utilisation measures against the
 * firm's expected daily hours and realisation against write-offs, neither of
 * which the widget spec can express. Everything comes from one `analytics`
 * read, so the whole set costs a single subscription.
 *
 * Used by:
 * - src/components/timesheets/TimesheetAnalytics.tsx
 * - src/components/timesheets/FavouriteReportsBoard.tsx
 */

import type { ReactNode } from "react";
import { addDays, format, parseISO } from "date-fns";
import { Bar, CartesianGrid, ComposedChart, XAxis, YAxis } from "recharts";

import type {
  BoardCardSize,
  OverviewCardKey,
} from "@cvx/timesheets/timesheetProfiles/schema";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import { formatAud as aud } from "./format";

/** Board order for a user who has never dragged anything. */
export const OVERVIEW_CARD_ORDER: OverviewCardKey[] = [
  "actual_time",
  "chargeable_time",
  "chargeable_value",
  "utilisation",
  "realisation",
  "awaiting_approval",
  "daily_hours",
];

export const OVERVIEW_CARD_TITLES: Record<OverviewCardKey, string> = {
  actual_time: "Actual time",
  chargeable_time: "Chargeable time",
  chargeable_value: "Chargeable value",
  utilisation: "Utilisation",
  realisation: "Realisation",
  awaiting_approval: "Awaiting approval",
  daily_hours: "Actual vs chargeable hours by day",
};

/** A metric is a tile; the chart wants the width of a chart. */
export function defaultOverviewSize(key: OverviewCardKey): BoardCardSize {
  return key === "daily_hours" ? "full" : "small";
}

export interface OverviewCard {
  key: OverviewCardKey;
  title: string;
  /** Tiles render a single number; panels draw their own body. */
  tile?: { value: string; sub?: string };
  panel?: ReactNode;
}

const chartConfig = {
  actualHours: { label: "Actual", color: "hsl(var(--primary))" },
  billableHours: { label: "Chargeable", color: "hsl(var(--chart-2))" },
} satisfies ChartConfig;

/** Weekdays in [start, end] no later than today — utilisation denominator. */
export function workdaysElapsed(
  start: string,
  end: string,
  today: string,
): number {
  let count = 0;
  let d = parseISO(start);
  const stop = parseISO(end < today ? end : today);
  while (d <= stop) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
    d = addDays(d, 1);
  }
  return count;
}

/** The `analytics` query result, narrowed to what the cards read. */
export interface OverviewData {
  unitMinutes: number;
  expectedDailyUnits: number;
  totals: {
    actualUnits: number;
    billableUnits: number;
    billableValue: number;
    approvedValue: number;
    writtenOffValue: number;
    submittedUnits: number;
    draftUnits: number;
  };
  byUser: unknown[];
  days: { date: string; actualUnits: number; billableUnits: number }[];
}

/**
 * Build every built-in card from one analytics read. The board decides which
 * of them are on the board and where; this decides what they say.
 */
export function buildOverviewCards({
  data,
  scope,
  start,
  end,
  today,
}: {
  data: OverviewData;
  scope: "me" | "org";
  start: string;
  end: string;
  today: string;
}): OverviewCard[] {
  const toHours = (units: number) => (units * data.unitMinutes) / 60;
  const fmtH = (units: number) =>
    `${toHours(units).toLocaleString("en-AU", { maximumFractionDigits: 1 })}h`;

  const headcount = scope === "org" ? Math.max(1, data.byUser.length) : 1;
  const capacityUnits =
    data.expectedDailyUnits * workdaysElapsed(start, end, today) * headcount;
  const utilisationPct =
    capacityUnits > 0
      ? Math.round((data.totals.actualUnits / capacityUnits) * 100)
      : null;
  const billablePct =
    data.totals.actualUnits > 0
      ? Math.round((data.totals.billableUnits / data.totals.actualUnits) * 100)
      : 0;
  // Realisation: how much approved billable value survived write-off review.
  const realisationPct =
    data.totals.approvedValue > 0
      ? Math.round(
          ((data.totals.approvedValue - data.totals.writtenOffValue) /
            data.totals.approvedValue) *
            100,
        )
      : null;

  const chartData = data.days.map((d) => ({
    date: format(parseISO(d.date), "d MMM"),
    actualHours: toHours(d.actualUnits),
    billableHours: toHours(d.billableUnits),
  }));

  const tile = (key: OverviewCardKey, value: string, sub?: string) => ({
    key,
    title: OVERVIEW_CARD_TITLES[key],
    tile: { value, sub },
  });

  return [
    tile("actual_time", fmtH(data.totals.actualUnits)),
    tile(
      "chargeable_time",
      fmtH(data.totals.billableUnits),
      `${billablePct}% of actual`,
    ),
    tile(
      "chargeable_value",
      aud(data.totals.billableValue),
      `${aud(data.totals.approvedValue)} approved`,
    ),
    tile(
      "utilisation",
      utilisationPct !== null ? `${utilisationPct}%` : "—",
      `of ${fmtH(capacityUnits)} capacity`,
    ),
    tile(
      "realisation",
      realisationPct !== null ? `${realisationPct}%` : "—",
      data.totals.writtenOffValue > 0
        ? `${aud(data.totals.writtenOffValue)} written off`
        : "no write-offs",
    ),
    tile(
      "awaiting_approval",
      fmtH(data.totals.submittedUnits),
      `${fmtH(data.totals.draftUnits)} still draft`,
    ),
    {
      key: "daily_hours",
      title: OVERVIEW_CARD_TITLES.daily_hours,
      panel:
        chartData.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No time recorded in this period.
          </p>
        ) : (
          <ChartContainer config={chartConfig} className="h-64 w-full">
            <ComposedChart data={chartData}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="date" tickLine={false} axisLine={false} />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={36}
                tickFormatter={(v: number) => `${v}h`}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                dataKey="actualHours"
                fill="var(--color-actualHours)"
                radius={[3, 3, 0, 0]}
              />
              <Bar
                dataKey="billableHours"
                fill="var(--color-billableHours)"
                radius={[3, 3, 0, 0]}
              />
            </ComposedChart>
          </ChartContainer>
        ),
    },
  ];
}
